import dottie from 'dottie';
import equal from 'fast-deep-equal';

// Adapted from https://stackoverflow.com/a/44198641
function isValidDate(date: string): boolean {
  return !Number.isNaN(Number(new Date(date)));
}

export function compare<T>(a: unknown, b: unknown, objectA: T, objectB: T): number {
  if (typeof a === 'number' && typeof b === 'number') {
    return a - b;
  }

  if (equal(a, b)) {
    return 0;
  }

  if (!a) {
    return 1;
  }

  if (!b) {
    return -1;
  }

  if (typeof a === 'string' && typeof b === 'string') {
    if (isValidDate(a) && isValidDate(b)) {
      return Number(new Date(a)) - Number(new Date(b));
    }

    return a.localeCompare(b, undefined, { numeric: true, usage: 'sort' });
  }

  console.error('Cannot compare', a, b, objectA, objectB);
  throw new Error('Implement comparator for values');
}

/**
 * Sorts a list objects using a specific comparator, ascending or not.
 * Note: Does not modify original array.
 * @param {Array<Object>} items The list of objects to sort
 * @param {String} sortByProperty The property to get to sort by on each object
 * @param {Object} [options] optional parameters
 * @param {Boolean} [options.ascending] Whether the sort is in ascending or descending order
 * @param {Boolean} [options.getValue] A function to retrieve the values to sort upon
 * @param {Function} [options.comparator] Compares two values, see JS sort method for return types.
 * @returns {Array<Object>} sorted items
 */
export default function sort<T extends Record<string, unknown>>(
  items: T[],
  sortBy: string | string[],
  {
    ascending = true,
    comparator = compare,
    getValue = (obj: T, property: string) => dottie.get(obj, property)
  } = {}
): T[] {
  const direction = ascending ? 1 : -1;

  if (!sortBy) {
    return items;
  }
  const allItems = items.slice();
  ([] as string[]).concat(sortBy).forEach(sortByProperty => {
    allItems.sort((a, b) => {
      const aVal = getValue(a, sortByProperty);
      const bVal = getValue(b, sortByProperty);

      try {
        return direction * comparator(aVal, bVal, a, b);
      } catch (err) {
        throw new Error(
          `Check comparator function: ${
            (err as Error).message
          }\nFailed to sort property: '${sortByProperty}'`
        );
      }
    });
  });

  return allItems;
}
